import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Check for Google OAuth code in query params
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');

        if (!code) {
          console.error('No authorization code found');
          setError('Código de autorização não encontrado. Por favor, tente fazer login novamente.');
          setTimeout(() => navigate('/auth'), 3000);
          return;
        }

        // Exchange code for user data
        const response = await api.post('/api/auth/google-callback', { code });

        const { user, session_token, token } = response.data;
        const authToken = token || session_token;

        // Store session token and update auth context
        login(authToken, user);

        // Redirect based on profile completion
        if (!user.profile_completed) {
          navigate('/complete-profile', { state: { user }, replace: true });
        } else {
          navigate('/dashboard', { state: { user }, replace: true });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        const errorMsg = error.response?.data?.detail || error.message || 'Erro desconhecido';
        setError(`Erro na autenticação: ${errorMsg}`);
        setTimeout(() => navigate('/auth'), 5000);
      }
    };

    processCallback();
  }, [navigate, login, location.search]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center max-w-md p-6">
          <div className="text-red-600 mb-4">
            <svg className="h-12 w-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="font-bold">Erro de Autenticação</p>
          </div>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <p className="text-xs text-gray-500">Redirecionando para login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p>Autenticando com Google...</p>
        <p className="text-sm text-gray-500 mt-2">Por favor, aguarde</p>
      </div>
    </div>
  );
};

export default AuthCallback;
